/**
 * Dossier imprimible para disputas Stripe (Print → Guardar como PDF).
 *
 * No sustituye asesoría legal ni garantiza resultado ante el emisor.
 */

export type DisputeDossierStripeSlice = {
  id: string;
  amountCents: number;
  currency: string;
  status: string;
  reason: string | null;
  createdUnix: number;
  dueByUnix: number | null;
  chargeId: string | null;
  paymentIntentId: string | null;
  customerEmail: string | null;
};

export type DisputeDossierInput = {
  merchantName: string;
  merchantPersonName: string;
  merchantUrl: string;
  supportEmail: string;
  generatedAtIso: string;
  generatedByEmail: string | null;
  stripeDashboardDisputeUrl: string;
  stripe: DisputeDossierStripeSlice;
  analysisNotes: string;
  evidenceSummary: string;
  internalStatus: string;
};

function escapeHtml(raw: string): string {
  return raw
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatMoney(amountCents: number, currency: string): string {
  try {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: currency.toUpperCase(),
      maximumFractionDigits: 2,
    }).format(amountCents / 100);
  } catch {
    return `${(amountCents / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

function formatUnix(ts: number | null): string {
  if (ts == null) return "—";
  try {
    return new Date(ts * 1000).toLocaleString("es-AR", {
      timeZone: "UTC",
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

/** Guía breve alineada a reason codes de Stripe (resumen operativo). */
function guidanceForReason(reason: string | null): string[] {
  const r = (reason ?? "").toLowerCase().trim();
  const base =
    "Requisitos oficiales por motivo: https://docs.stripe.com/disputes/reason-codes-defense-requirements";
  if (r === "fraudulent") {
    return [
      "En “fraude” el emisor suele evaluar si el titular participó en el cobro que ganó: autenticación (p. ej. 3DS) cuando aplique, coincidencia de email/datos, y uso posterior coherente del servicio.",
      "El timeline de intentos fallidos en la misma sesión puede reforzar intención de compra, pero no reemplaza prueba sobre el cargo disputado concreto (p. ej. datos del pago exitoso).",
      "Enmarcar con cuidado bloqueos de Radar en otros intentos: contexto de sesión, no “prueba de legitimidad” por sí solos.",
      base,
    ];
  }
  if (r === "product_not_received") {
    return [
      "En digital, “recibido” suele significar acceso o servicio puesto a disposición: fechas de activación, confirmaciones, logs de uso razonablemente agregados.",
      base,
    ];
  }
  if (r === "product_unacceptable") {
    return [
      "Aportar descripción del servicio entregado, términos aceptados si los hay, y hechos objetivos (fechas, canales de soporte, intentos de resolución).",
      base,
    ];
  }
  if (r === "subscription_canceled" || r === "subscription_cancelled") {
    return [
      "Documentar política de cancelación, comunicaciones de renovación y qué correspondía cobrar según el periodo disputado.",
      base,
    ];
  }
  if (r === "duplicate") {
    return [
      "Demostrar que hubo una sola entrega de bienes/servicio o un solo cobro legítimo por el concepto (IDs de pago, facturas, periodos distintos).",
      base,
    ];
  }
  if (r === "credit_not_processed") {
    return [
      "Evidenciar devoluciones aplicadas, política de reembolso y registros de que no corría un crédito pendiente al disputar.",
      base,
    ];
  }
  return [
    "Revisar el checklist de Stripe para el motivo indicado y adjuntar solo lo que corresponda (evitar paquetes genéricos irrelevantes).",
    base,
  ];
}

/** Bloques que Stripe suele mostrar en Counter / formulario de evidencia (nombres orientativos). */
function stripeEvidenceBlocksTableHtml(): string {
  const rows: [string, string][] = [
    [
      "Pago, cliente y negocio",
      "Charge, Customer, email de facturación, descriptor; Smart Disputes a menudo prellena parte. En este dossier: §1.",
    ],
    [
      "Historial en la red Stripe",
      "Señales agregadas que usa Stripe internamente; no las cargás vos como archivo.",
    ],
    [
      "Envío (shipping)",
      "Pensado para producto físico. Si ofrecés solo digital: no inventar tracking; indicar entrega digital / acceso según permita el formulario.",
    ],
    [
      "Descripción del producto o servicio",
      "Qué se vendió, modalidad (suscripción / pago único), nombre del plan. Completar §4 y pegar en Stripe si hay campo equivalente.",
    ],
    [
      "Comunicación con el cliente",
      "Emails de confirmación, soporte, Telegram si aplica al mismo email/identidad. Resumir en §5 o adjuntar capturas en Stripe (§6).",
    ],
    [
      "Servicio prestado / acceso",
      "Fechas de activación de membresía, uso razonable (logs agregados). §5; archivos en §6.",
    ],
    [
      "Políticas (reembolso / cancelación)",
      "URL de términos o pantallazo de política vigente al momento del pago. Mencionar en §6.",
    ],
  ];
  return rows
    .map(
      ([th, td]) =>
        `<tr><th>${escapeHtml(th)}</th><td>${escapeHtml(td)}</td></tr>`,
    )
    .join("\n");
}

/** Párrafos guía para copiar en campos de texto de Stripe (solo hechos verificables). */
function pasteTemplatesForReason(reason: string | null): string {
  const r = (reason ?? "").toLowerCase().trim();
  const commonProduct =
    "Producto/servicio: [membresía digital / acceso a …]. Modalidad: [suscripción mensual | pago único]. " +
    "El cliente completó el checkout en [dominio / flujo]. El importe y la descripción coinciden con lo mostrado antes de pagar.";
  const commonTail =
    "\n\nDatos de referencia: Dispute [dp_…], Charge [ch_…], PaymentIntent [pi_…]. " +
    "Evidencia adjunta en Stripe: [listar: recibo, políticas, comunicaciones, etc.].";

  let body = "";
  if (r === "fraudulent") {
    body =
      "El titular completó un flujo de pago legítimo para [producto]. " +
      "El email de facturación / cuenta utilizada en el checkout coincide con [hecho verificable: mismo email que registro / soporte]. " +
      "Tras el pago se [activó acceso / se envió confirmación / hubo uso del servicio el día …]. " +
      "Solicitamos que se tenga en cuenta la participación del titular en el cobro que fue capturado exitosamente (p. ej. datos de autenticación o método guardado según conste en Stripe)." +
      commonTail;
  } else if (r === "product_not_received") {
    body =
      "El servicio digital contratado fue puesto a disposición del cliente: [fecha UTC, canal: acceso a grupo / plataforma / enlace]. " +
      "Confirmación o registro interno: [referencia sin PII innecesaria]. " +
      "El cargo corresponde a [descripción breve del periodo o entrega]." +
      commonTail;
  } else if (r === "product_unacceptable") {
    body =
      "El servicio entregado fue el acordado en el checkout: [descripción]. " +
      "Los términos aplicables estaban disponibles en [URL] al momento de la compra. " +
      "Ofrecimos soporte el [fecha] vía [canal]." +
      commonTail;
  } else if (r === "subscription_canceled" || r === "subscription_cancelled") {
    body =
      "El cargo corresponde a un periodo de suscripción [activo / no cancelado antes del cierre de facturación] según [política / fecha de cancelación en Stripe o en cuenta]. " +
      "Las comunicaciones de renovación o factura están disponibles como [adjunto / en panel del cliente]." +
      commonTail;
  } else {
    body =
      commonProduct +
      "\n\nMotivo del dispute según Stripe: [" +
      (reason ?? "—") +
      "]. " +
      "Los hechos que sostienen nuestra respuesta son: [enumerar]." +
      commonTail;
  }

  return `<h3>Borrador para campo de texto en Stripe (releer y recortar)</h3>
<p class="muted">Sustituí los corchetes por datos reales. No pegar PII que no vaya a adjuntarse también como prueba permitida.</p>
<pre class="block">${escapeHtml(body)}</pre>`;
}

export function buildDisputeDossierHtml(input: DisputeDossierInput): string {
  const reasonLabel = input.stripe.reason
    ? input.stripe.reason.replace(/_/g, " ")
    : "—";
  const guidance = guidanceForReason(input.stripe.reason);
  const notes = escapeHtml(input.analysisNotes.trim() || "(Sin notas internas.)");
  const evidence = escapeHtml(
    input.evidenceSummary.trim() || "(Sin resumen de evidencia aún.)",
  );

  const guidanceHtml = guidance
    .map((line) => `<li>${escapeHtml(line)}</li>`)
    .join("\n");

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Dossier disputa ${escapeHtml(input.stripe.id)}</title>
  <style>
    :root { font-family: ui-sans-serif, system-ui, "Segoe UI", Roboto, sans-serif; color: #111; }
    body { margin: 0; padding: 12mm 14mm; max-width: 210mm; line-height: 1.45; font-size: 11pt; }
    h1 { font-size: 18pt; margin: 0 0 8px; }
    h2 { font-size: 12.5pt; margin: 20px 0 8px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
    .muted { color: #555; font-size: 10pt; }
    .warn { background: #fff8e6; border: 1px solid #e6c35c; padding: 10px 12px; margin: 14px 0; font-size: 10pt; }
    table { width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 10.5pt; }
    th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; vertical-align: top; }
    th { background: #f5f5f5; width: 32%; }
    pre.block { white-space: pre-wrap; font-family: inherit; margin: 8px 0; font-size: 10.5pt; }
    ul { margin: 8px 0; padding-left: 20px; }
    a { color: #0b5; word-break: break-all; }
    @media print {
      body { padding: 10mm 12mm; }
      .no-print { display: none !important; }
      h2 { break-after: avoid; }
      tr { break-inside: avoid; }
    }
  </style>
</head>
<body>
  <p class="no-print muted">Usá <strong>Archivo → Imprimir → Guardar como PDF</strong> (o el cuadro de impresión del navegador).</p>

  <h1>Dossier de evidencia — Disputa Stripe</h1>
  <p class="muted">
    Comercio: <strong>${escapeHtml(input.merchantName)}</strong>
    ${input.merchantPersonName ? ` · ${escapeHtml(input.merchantPersonName)}` : ""}
    · Generado: ${escapeHtml(input.generatedAtIso)} UTC
    ${input.generatedByEmail ? ` · Por: ${escapeHtml(input.generatedByEmail)}` : ""}
  </p>

  <div class="warn">
    <strong>Aviso:</strong> documento operativo para organizar la respuesta en Stripe
    (Counter / Smart Disputes o envío manual). No es asesoramiento legal; el resultado lo define el emisor.
    Minimizá PII en adjuntos. Revisá el checklist oficial antes de enviar.
  </div>

  <h2>Uso rápido con Counter Stripe</h2>
  <p class="muted">
    En el asistente (“Step 1 of 2”) elegí opciones que sean <strong>ciertas</strong>
    (p. ej. “cardholder received the product or service” solo si podés probarlo).
    Tipo de producto: para membresía/acceso digital suele ser <strong>Digital product or service</strong>.
    El texto de <strong>§3</strong> (borrador para pegar) y <strong>§4</strong> (detalle del producto), más los datos de <strong>§1</strong>, los podés copiar o condensar en los campos que Stripe muestre en el paso 2.
  </p>

  <h2>1. Identificadores Stripe</h2>
  <table>
    <tr><th>Dispute</th><td><code>${escapeHtml(input.stripe.id)}</code></td></tr>
    <tr><th>Estado disputa</th><td>${escapeHtml(input.stripe.status.replace(/_/g, " "))}</td></tr>
    <tr><th>Motivo (reason)</th><td>${escapeHtml(reasonLabel)}</td></tr>
    <tr><th>Monto disputado</th><td>${escapeHtml(formatMoney(input.stripe.amountCents, input.stripe.currency))}</td></tr>
    <tr><th>Creada (UTC)</th><td>${escapeHtml(formatUnix(input.stripe.createdUnix))}</td></tr>
    <tr><th>Evidencia hasta (UTC)</th><td>${escapeHtml(formatUnix(input.stripe.dueByUnix))}</td></tr>
    <tr><th>Charge</th><td>${input.stripe.chargeId ? `<code>${escapeHtml(input.stripe.chargeId)}</code>` : "—"}</td></tr>
    <tr><th>PaymentIntent</th><td>${input.stripe.paymentIntentId ? `<code>${escapeHtml(input.stripe.paymentIntentId)}</code>` : "—"}</td></tr>
    <tr><th>Email en cargo (facturación)</th><td>${input.stripe.customerEmail ? escapeHtml(input.stripe.customerEmail) : "—"}</td></tr>
    <tr><th>Dashboard Stripe</th><td><a href="${escapeHtml(input.stripeDashboardDisputeUrl)}">${escapeHtml(input.stripeDashboardDisputeUrl)}</a></td></tr>
  </table>
  <p class="muted">Sitio público / soporte: ${escapeHtml(input.merchantUrl)}${input.supportEmail ? ` · ${escapeHtml(input.supportEmail)}` : ""}</p>

  <h2>2. Equivalencia con bloques de evidencia Stripe</h2>
  <p class="muted">
    Smart Disputes y el formulario clásico suelen organizar la prueba en categorías parecidas a las siguientes.
    Usá esta tabla para saber qué completar en el Dashboard y qué sección de este PDF usar como borrador.
  </p>
  <table>
    ${stripeEvidenceBlocksTableHtml()}
  </table>

  <h2>3. Guía según motivo (<code>reason</code>)</h2>
  <ul>${guidanceHtml}</ul>

  ${pasteTemplatesForReason(input.stripe.reason)}

  <h2>4. Detalle del producto / servicio (para “Product or service details”)</h2>
  <p class="muted">
    Debe coincidir con Checkout y con lo que el cliente vio: nombre comercial, plan, renovación si aplica, canal de entrega (p. ej. acceso digital).
  </p>
  <pre class="block">Nombre comercial / plan: […]
Modalidad: [suscripción recurrente | pago único]
Qué recibe el cliente: […]
Dónde se entrega (digital): [grupo / enlace / plataforma]
URL de términos o información previa al pago: ${escapeHtml(input.merchantUrl)}</pre>

  <h2>5. Análisis interno y línea de tiempo</h2>
  <p class="muted">Timeline del PaymentIntent (desde Stripe), correlación usuario/sesión (PostHog/Clerk/DB), hechos con fechas UTC.</p>
  <pre class="block">${notes}</pre>

  <h2>6. Evidencia adjunta / enviada en Stripe</h2>
  <p class="muted">Listá archivos o capturas que subiste o subirás (recibo, emails, políticas, uso agregado).</p>
  <pre class="block">${evidence}</pre>

  <h2>7. Estado interno (panel admin)</h2>
  <p>${escapeHtml(input.internalStatus)}</p>

  <p class="muted" style="margin-top: 28px;">Fin del documento · ${escapeHtml(input.merchantName)}</p>
</body>
</html>`;
}

/**
 * Abre una ventana con el HTML y dispara el diálogo de impresión (Guardar como PDF).
 */
export function openDisputeDossierPrintWindow(html: string): boolean {
  const w = window.open("", "_blank", "noopener,noreferrer");
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  let fired = false;
  const triggerPrint = () => {
    if (fired) return;
    fired = true;
    try {
      w.focus();
      w.print();
    } catch {
      /* noop */
    }
  };
  w.addEventListener("load", triggerPrint, { once: true });
  window.setTimeout(triggerPrint, 600);
  return true;
}
